import { CleaveOptions } from 'cleave.js/options';
import { PseudoBool } from './VoPayApi.types';

export enum OnboardingResponse {
  ApiResponse = 'ApiResponse',
  AuthenticateUserResponse = 'AuthenticateUserResponse',
  AlphaVantageResponse = 'AlphaVantageResponse',
};

export type TextFieldType = {
  type: 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url';
};

export type TextFieldPreset = TextFieldType & {
  options: CleaveOptions;
};

export type TextFieldPresets = {
  currency: TextFieldPreset;
  text: TextFieldType;
  numeric: TextFieldPreset;
  numericWholePositive: TextFieldPreset;
  password: TextFieldType;
  email: TextFieldType;
  date: TextFieldPreset;
  number: TextFieldPreset;
  creditCard: TextFieldPreset;
  private: TextFieldType;
  googleAddressLine1: TextFieldType;
  googleAddressCity: TextFieldType;
  googleAddressProvince: TextFieldType;
  googleAddressPostalCode: TextFieldType;
  googleAddressCountry: TextFieldType;
  fullName: TextFieldType;
};

export type ApiResponseBase = {
  success: boolean;
  errorMessage: string;
  message?: string;
};

export type OnboardingStatusResponse = {
  StatusCode: number;
};

export type OnboardingApiResponse = {
  StatusCode: number;
  Headers: {
    'Content-Type': string;
  };
};

export type OnboardingApplicationRequest = {
  applicationToken: string;
};

export type OnboardingLanguageRequest = {
  language?: string;
};

export type MerchantThemeResponse = ApiResponseBase &
  OnboardingApiResponse & {
    name: string;
    primaryColor: string;
    secondaryColor: string;
    brandingLogo: string;
  };

export type LanguageResponse = ApiResponseBase & {
  languages: {
    [Key: string]: {
      language: string;
      isDefault: number;
    }
  }
};

export type GenerateOTPBodyRequest = {
  email: string;
};

export type GenerateOTPResponse = ApiResponseBase &
  OnboardingApiResponse & {
    LockedMinutes?: number;
    email: string;
    name: string;
    role: string;
  };

export type AuthenticateUserBodyRequest = {
  email: string;
  OTP: string;
};

export type AuthenticateUserResponse = ApiResponseBase &
  OnboardingStatusResponse & {
    bearerToken: string;
    submitted?: number;
    completedRedirectUrl?: string;
    applicationsId: number;
  };

export type OnboardingProgressResponse = ApiResponseBase & {
  progress: number;
  maxEstimatedTimeRemaining: number;
  minEstimatedTimeRemaining: number;
  submitted?: number;
  completedRedirectUrl?: string;
};

export type ApplicationFormReviewList = {
  formsId: number;
  name: string;
  translatedName: string;
  identifier: string;
  description: string;
  url: string;
  formTemplatesId: number;
  formTemplatesIdentifier: string;
  status: string;
  values: {
    [Key: string]: {
      formFieldsId: number;
      identifier: string;
      fieldRow: number;
      fieldColumn: number;
      fieldWidth: number;
      label: string;
      inputType: string;
      preset: string;
      required: number;
      placeholder: string;
      tooltip: string;
      triggerWebhookOnChange: number;
      parentFormFieldsId: number;
      formValuesId: number;
      value: string;
      options: { [key: string]: string };
    };
  };
};

export type ApplicationReviewResponse = ApiResponseBase & {
  forms: { [Key: string]: ApplicationFormReviewList };
  documents: { [Key: string]: DocumentFieldsList };
};

export type ApplicationFormList = {
  formsId: number;
  name: string;
  translatedName: string;
  description: string;
  url: string;
  formTemplatesId: number;
  formTemplatesIdentifier: string;
  formTemplatesFormRow: number;
  status: string;
};

export type ApplicationFormsResponse = ApiResponseBase & {
  completedRedirectUrl?: string;
  forms: { [Key: string]: ApplicationFormList };
};

export type ApplicationDocumentList = {
  documentsId: number;
  name: string;
  translatedName: string;
  description: string;
  sampleAvailable: number;
  documentTemplatesId: number;
  documentTemplatesIdentifier: string;
  documentTemplatesDocumentRow: number;
  status: string;
};

export type ApplicationDocumentsResponse = ApiResponseBase &
  OnboardingStatusResponse & {
    documents: { [Key: string]: ApplicationDocumentList };
  };

export type DocumentTemplateRequest = {
  documentTemplatesId: number;
};

export type DocumentTemplateResponse = ApiResponseBase & {
  documentTemplatesId: number;
  name: string;
  description: string;
  sample: string;
};

export type ApplicationFormRequest = OnboardingLanguageRequest & {
  formsId: number;
  language?: string;
};
export type SaveFormResponse = ApiResponseBase & {
  applicationsId?: number;
  formsId?: number;
  name?: string;
  translatedName?: string;
  description?: string;
  url?: string;
  formTemplatesId?: number;
  formTemplatesIdentifier?: string;
  values?: { [Key: string]: ApplicationFormFieldsList };
};

export type ApplicationFormResponse = ApiResponseBase & {
  applicationsId: number;
  formsId: number;
  name: string;
  translatedName: string;
  description: string;
  url: string;
  formTemplatesId: number;
  formTemplatesIdentifier: string;
  values: { [Key: string]: ApplicationFormFieldsList };
};

export type ApplicationFormFieldsList = {
  formFieldsId: number;
  formFieldsIdentifier: string;
  fieldRow: number;
  fieldColumn: number;
  fieldWidth: number;
  label: string;
  inputType: string;
  preset: keyof TextFieldPresets | undefined;
  required: number;
  placeholder: string;
  tooltip: string;
  maxLength?: number;
  isReadOnly: number;
  triggerWebhookOnChange: number;
  parentFormFieldsId: number;
  formValuesId: number;
  value: string;
  options: any;
};

export type ApplicationDocumentRequest = {
  documentsId: number;
  language?: string;
};

export type DocumentFieldsList = {
  documentsId: number;
  name: string;
  translatedName: string;
  description: string;
  documentTemplatesId: number;
  documentTemplatesIdentifier: string;
  value: string;
  omitted: number;
  omittedReason: string | null;
  status: string;
};

export type ApplicationDocumentResponse = ApiResponseBase & {
  document: DocumentFieldsList;
};

export type UploadDocumentBodyRequest = {
  documentsId: number;
  document: string;
  fileName: string;
  omittedReason: string;
  language: string;
};

export type UploadDocumentResponse = ApiResponseBase & {
  documentsId: number;
};

export type SaveFormFieldsList = {
  formFieldsId: number;
  value: string;
};

export type SaveFormBodyRequest = {
  formValuesId?: number;
  language?: string;
  formsId: number;
  fields: SaveFormFieldsList[];
};

export type HeartbeatFormBodyRequest = {
  formsId: number;
};

export type HeartbeatDocumentBodyRequest = {
  documentsId: number;
};

export type AddCollaboratorBodyRequest = {
  name: string;
  email: string;
  formsId?: number;
  documentsId?: number;
};
export type AddCollaboratorBodyForm = {
  name: string;
  email: string;
  formsId: string;
  documentsId: string;
  formAndDocuId: string;
  collaboratorDropdown: string;
};

export type CollaboratorsRequest = OnboardingLanguageRequest & {
  collaboratorsId?: number;
  role?: string;
  language?: string;
};

export type CollaboratorFormsList = {
  formsId: number;
  name: string;
  translatedName: string;
  description: string;
  translatedDescription: string;
  url: string;
  formTemplatesId: number;
  formTemplatesIdentifier: string;
  formTemplatesFormRow: number;
  status: string;
};

export type CollaboratorDocumentsList = {
  documentsId: number;
  name: string;
  translatedName: string;
  description: string;
  sampleAvailable: number;
  documentTemplatesId: number;
  documentTemplatesIdentifier: string;
  documentTemplatesDocumentRow: number;
  status: string;
};

export type CollaboratorsResponse = ApiResponseBase & {
  collaborators: {
    [key: string]: {
      id: string;
      name: string;
      email: string;
      role: string;
      forms: { [key: string]: CollaboratorFormsList };
      documents: { [key: string]: CollaboratorDocumentsList };
    };
  };
  viewOrientation: 'horizontal' | 'vertical';
};

export type MerchantInfoResponse = ApiResponseBase & {
  id: number;
  merchantsName: string;
  adminCollaboratorName: string;
};

export type ReInviteCollaboratorBodyRequest = {
  email: string;
};

export type ApplicationCardsList = {
  cardsId: number;
  label: string;
  type: string;
  description: string;
  descriptionNote: string;
  buttonLabel: string;
  buttonImage: string;
  url: string;
};

export type ApplicationCardsResponse = ApiResponseBase & {
  cards: { [key: string]: ApplicationCardsList };
};

export type SubmitApplicationResponse = ApiResponseBase & {
  completedRedirectUrl: string;
  applicationSubmitted: boolean;
};

export type AlphaVantageRequest = {
  keywords: string;
  apikey: string;
};

export type CompanySymbolList = {
  "1. symbol": string;
  "2. name": string;
  "3. type": string;
  "4. region": string;
  "5. marketOpen": string;
  "6. marketClose": string;
  "7. timezone": string;
  "8. currency": string;
  "9. matchScore": string;
}[];

export type AlphaVantageResponse = {
  bestMatches: { [key: string]: CompanySymbolList };
};

export type ApplicationsList = {
  applications: Application[];
};
export type Application = {
  id: string;
  user: string;
  accountname: string;
  emailAddress: string;
  sent: string;
  reminderSent: string;
  expires: string;
  applicationProgress: number;
  isExpanded: boolean;
  lastActivity?: string;
  forms: {
    name: string;
    progress: number;
    formsId: number;
    status: string;
  }[];
};
export type GenerateOTPMerchantRequest = {
  email: string;
};

export type GenerateOTPMerchantResponse = ApiResponseBase &
  OnboardingApiResponse & {
    LockedMinutes?: number;
  };

export type AuthenticateUserMerchantRequest = {
  email: string;
  OTP: string;
};

export type AuthenticateUserMerchantResponse = ApiResponseBase &
  OnboardingApiResponse & {
    merchantUserToken: string;
  };
export type MerchantApplication = {
  id: string;
  applicantCompany: string;
  lastActive?: string | null;
  submitted: number;
  expiresAt: string;
  createdAt: string;
  adminCollaboratorName: string;
  adminCollaboratorEmail: string;

  merchantsId: string;
};

export type MerchantApplicationsRequest = {
  fromDate: string;
  toDate: string;
}

export type MerchantApplicationsResponse = ApiResponseBase &
  OnboardingApiResponse & {
    applications: MerchantApplication[];
  };

export type MerchantApplicationProgressRequest = {
  applicationsId: string;
}

export type ApplicationProgressForm = {
  name: string;
  progress: number;
}

export type ApplicationProgressDocument = {
  name: string;
  status: "completed" | "not started" | "in progress"; // Adjust status options as needed
}

export type MerchantApplicationProgressResponse = {
  success: boolean;
  errorMessage: string;
  applicationProgress: number;
  lastSentReminderEmail?: string | null;
  forms: ApplicationProgressForm[];
  documents: ApplicationProgressDocument[];
};
export type RenewApplicationRequest = {
  applicationsId: string;
  renew: PseudoBool;
}
export type RenewApplicationResponse = ApiResponseBase &
  OnboardingApiResponse & {
    expiresAt: string
  }

export type SendReminderRequest = {
  applicationsId: string;
  sendEmail: PseudoBool;
  emailTemplate: "welcome" | "reminder";
}
export type SendReminderResponse = ApiResponseBase &
  OnboardingApiResponse & {
    expiresAt: string
  }

